import React from 'react';
import {graphql} from 'react-relay';
import {useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import Styled from 'styled-components/macro';
import {Breadcrumbs, Link} from '@forecast-it/design-system';
import {PageHeader} from '@/web-components/core/page-header';
import {ForecastQueryRenderer} from '../../ForecastQueryRenderer';
import {hasFeatureFlag} from '../shared/util/FeatureUtil';
import {OpportunitiesList} from '../opportunities-tab/OpportunitiesPage';
import {clientLogoSrc} from '../../directApi';
import {FEATURE_FLAGS} from '../../constants';
import type {OpportunityFilterTab} from '../opportunities-tab/opportunityFilters';

// Client-specific empty-state copy. One message per filter tab; overrides the global defaults
// via the OpportunitiesList emptyStateMessageIds contract prop.
const CLIENT_OPPORTUNITY_EMPTY_MESSAGE_IDS: Record<OpportunityFilterTab, string> = {
	open: 'opportunities.client_empty.open',
	won: 'opportunities.client_empty.won',
	lost: 'opportunities.client_empty.lost',
	won_unconverted: 'opportunities.client_empty.won_unconverted',
	all: 'opportunities.client_empty.all',
};

// Status-badge colours, mirroring the client Overview header (active = on-track green, inactive = grey).
const COLOR_STATUS_ACTIVE = '#27bb8f';
const COLOR_STATUS_INACTIVE = '#a1a1b0';
const COLOR_TEXT_MAIN = '#232334';

type ClientHeaderData = {
	id: string;
	name: string;
	logoId: string | null;
	status: {label: string; standing: 'ACTIVE' | 'INACTIVE'} | null;
};

const PageContent = Styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	width: 100%;
`;

// Header sits above the list with the same 32px horizontal rhythm as the list body and the Overview page.
const HeaderBar = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 8px 32px 16px 32px;
`;

const TitleRow = Styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

const ClientLogo = Styled.img`
	width: 32px;
	height: 32px;
	border-radius: 6px;
	object-fit: cover;
	flex-shrink: 0;
`;

const StatusBadge = Styled.span<{standing: 'ACTIVE' | 'INACTIVE'}>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	margin-left: auto;
	padding: 6px 12px;
	border: 1px solid ${({standing}) => (standing === 'ACTIVE' ? COLOR_STATUS_ACTIVE : COLOR_STATUS_INACTIVE)};
	border-radius: 8px;
	font-size: 14px;
	line-height: 20px;
	color: ${COLOR_TEXT_MAIN};
	background-color: #ffffff;
`;

const StatusDot = Styled.span<{standing: 'ACTIVE' | 'INACTIVE'}>`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: ${({standing}) => (standing === 'ACTIVE' ? COLOR_STATUS_ACTIVE : COLOR_STATUS_INACTIVE)};
`;

const ListArea = Styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	/* The reusable opportunities list carries 32px of top page padding for its standalone page. Here it
	   sits directly under this tab's client header, so neutralise that top padding to keep the gap tight
	   (owned here rather than in the shared list so it survives updates to that component). */
	[data-cy='opportunities-page'] {
		padding-top: 0;
	}
`;

// Breadcrumb (All Clients › <client> › Opportunities) + client logo/name/status shown above the reused
// opportunities list, matching the client-detail page chrome. The final crumb (Opportunities) is the
// current page, so it is plain text; the client name links back to the client Overview.
const CompanyOpportunitiesHeader = ({client, clientId}: {client: ClientHeaderData | null; clientId: string}): JSX.Element => {
	const {formatMessage} = useIntl();
	const history = useHistory();
	const clientsListPath = hasFeatureFlag(FEATURE_FLAGS.NEW_CLIENTS_FEATURE) ? '/clients' : '/admin/clients';
	const clientName = client?.name ?? '';
	const status = client?.status ?? null;
	// Keep the href for accessibility / open-in-new-tab, but do a client-side push on a plain left-click
	// so the crumbs navigate within the SPA instead of triggering a full page reload.
	const spaNavigate = (path: string) => (event: React.MouseEvent) => {
		if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
			event.preventDefault();
			history.push(path);
		}
	};
	return (
		<HeaderBar>
			<Breadcrumbs>
				<Breadcrumbs.Item>
					<Link href={clientsListPath} onClick={spaNavigate(clientsListPath)}>
						{formatMessage({id: 'company_view_page.breadcrumb_all_clients'})}
					</Link>
				</Breadcrumbs.Item>
				<Breadcrumbs.Item>
					<Link href={`/clients/${clientId}`} onClick={spaNavigate(`/clients/${clientId}`)}>
						{clientName}
					</Link>
				</Breadcrumbs.Item>
				<Breadcrumbs.Item>
					<span>{formatMessage({id: 'company_view_page.nav_opportunities'})}</span>
				</Breadcrumbs.Item>
			</Breadcrumbs>
			<TitleRow data-cy="company-opportunities-title">
				{client?.logoId ? (
					<ClientLogo crossOrigin="use-credentials" data-private src={clientLogoSrc(client.logoId)} alt="" />
				) : null}
				<PageHeader>{clientName}</PageHeader>
				{status ? (
					<StatusBadge standing={status.standing} data-cy="company-opportunities-client-status">
						<StatusDot standing={status.standing} />
						{status.label}
					</StatusBadge>
				) : null}
			</TitleRow>
		</HeaderBar>
	);
};

// The client-detail Opportunities tab. Owns the query boundary and mounts the reusable OpportunitiesList
// scoped to the current client, beneath a breadcrumb + client-name header matching the client-detail page
// chrome. The query fetches the client header fields alongside the same viewer/projects payload the list
// consumes. clientId is the Relay global id from the /clients/:clientId route, which equals node.client.id
// in the payload, so the list's in-memory client filter matches directly. Currency-variable derivation
// mirrors the global wrapper so the correct financial fragment is included.
export const CompanyViewOpportunitiesQuery = graphql`
	query CompanyViewOpportunities_Query($clientId: String, $loadSingleCurrency: Boolean!, $loadMultiCurrency: Boolean!) {
		viewer {
			id
			actualPersonId
			company {
				id
				currency
				skillLevelsEnabled
				opportunityStageBands(first: 1000) {
					edges {
						node {
							id
							name
							startPercentage
							endPercentage
							winProbability
						}
					}
				}
				client(id: $clientId) {
					id
					name
					logoId
					status {
						label
						standing
					}
				}
			}
			filters(first: 1000000, filterSection: OPPORTUNITIES) @connection(key: "Viewer_filters", filters: []) {
				edges {
					node {
						id
						name
						value
						section
						updatedAt
					}
				}
			}
			projects(first: 1000000, excludeRestricted: true) {
				edges {
					node {
						id
						customProjectId
						...ProjectsLoader_project @relay(mask: false)
						...OpportunitiesList_project @relay(mask: false)
						...ProjectsLoader_financialsMultiCurrencies @include(if: $loadMultiCurrency) @relay(mask: false)
						...ProjectsLoader_financialsCompanyCurrency @include(if: $loadSingleCurrency) @relay(mask: false)
					}
				}
			}
		}
	}
`;

type Props = {
	clientId: string;
};

export const CompanyViewOpportunities = ({clientId}: Props): JSX.Element => {
	// Picks which financial fragment the query includes: the single-currency one when the company loads
	// project financials in company currency, the multi-currency one otherwise. Exactly one is always true,
	// because the Target Value column reads `financialNumbers.baselineRevenue` from whichever is included —
	// leaving both false makes the column permanently blank (and its sort a no-op), since nothing else on
	// this tab selects that field. This deliberately differs from the Projects list, which additionally
	// suppresses both fragments when the financial-categories flag is on: that list also drops its financial
	// columns in that mode, so it has no value left to fetch. The Opportunities list keeps Target Value in
	// every mode and so always needs the data.
	// Narrowed by IGNITE-640: Target Value is no longer sourced from financialNumbers alone — T&M rows read
	// ProjectType.baselineTarget, which rides OpportunitiesList_project and is outside this @include gate.
	// So getting this wrong now blanks Fixed Fee rows only, leaving T&M rows rendering: a partially blank
	// column, subtler than the fully blank one this invariant was written for. See 003 D12.
	const loadSingleCurrency = hasFeatureFlag('projects_overview_load_single_currency');
	const loadMultiCurrency = !hasFeatureFlag('projects_overview_load_single_currency');
	return (
		<ForecastQueryRenderer
			key={'query-render-company-opportunities-' + clientId}
			query={CompanyViewOpportunitiesQuery}
			fetchPolicy="store-and-network"
			variables={{clientId, loadSingleCurrency, loadMultiCurrency}}
			render={(relayProps, retry) => (
				<PageContent>
					<CompanyOpportunitiesHeader client={relayProps?.viewer?.company?.client ?? null} clientId={clientId} />
					<ListArea>
						<OpportunitiesList
							{...relayProps}
							retry={retry}
							clientId={clientId}
							preferenceScope="opportunities-client"
							syncTabToUrl={false}
							emptyStateMessageIds={CLIENT_OPPORTUNITY_EMPTY_MESSAGE_IDS}
						/>
					</ListArea>
				</PageContent>
			)}
		/>
	);
};

export default CompanyViewOpportunities;
